import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabaseClient';
import { useNavigate } from 'react-router-dom';
import {
  FaExclamationTriangle, FaSearch, FaTimes, FaFileCsv, FaSpinner,
  FaCar, FaUser, FaTag, FaCalendarAlt, FaInfoCircle, FaEye
} from 'react-icons/fa';

/**
 * Schermata SOLO PER BROKER: elenco delle adesioni SCARTATE (tabella public.polizze_scartate).
 * La rotta è annidata sotto /dashboard/broker, che già impedisce l'accesso ai ruoli != 'broker'.
 */
export default function PolizzeScartate() {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const rowsPerPage = 25;

  // opzioni di lookup
  const [agenzieOptions, setAgenzieOptions] = useState([]);
  const [pacchettiById, setPacchettiById] = useState({}); // id -> nome
  const [motivoOptions, setMotivoOptions] = useState([]);

  // filtri (draft = UI, filters = query)
  const emptyFilters = {
    cliente: '',       // cognome_cliente / ragione_sociale
    codice_fiscale: '',
    targa: '',
    motivo_scarto: '',
    data_da: '',
    data_a: '',
  };
  const [draftFilters, setDraftFilters] = useState(emptyFilters);
  const [filters, setFilters] = useState(emptyFilters);

  // modale semplice per errori/avvisi
  const [modal, setModal] = useState({ open: false, title: '', message: '' });
  const openModal = (title, message) => setModal({ open: true, title, message });
  const closeModal = () => setModal({ open: false, title: '', message: '' });

  // modale di DETTAGLIO della singola adesione scartata
  const [detailRow, setDetailRow] = useState(null);

  const formatEuro = (value) => {
    const n = Number(value ?? 0);
    return Number.isFinite(n)
      ? n.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : '0,00';
  };
  const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString('it-IT', { year: 'numeric', month: '2-digit', day: '2-digit' }) : '';
  const formatDateTime = (d) =>
    d ? new Date(d).toLocaleDateString('it-IT', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';

  const isISODate = (s) => typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s);
  const toEndExclusiveISO = (yyyy_mm_dd) => {
    const d = new Date(yyyy_mm_dd);
    d.setDate(d.getDate() + 1);
    return d.toISOString();
  };

  // Recupera tutte le righe di una query (paginando internamente)
  const fetchAllRows = async (queryBuilder) => {
    const all = [];
    let from = 0;
    const limit = 1000;
    let more = true;
    while (more) {
      const { data, error } = await queryBuilder.range(from, from + limit - 1);
      if (error) throw error;
      if (!data || data.length === 0) { more = false; }
      else {
        all.push(...data);
        from += limit;
        if (data.length < limit) more = false;
      }
    }
    return all;
  };

  // --- Dati iniziali: guardia sessione + lookup ---
  useEffect(() => {
    const init = async () => {
      try {
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        if (userError) throw userError;
        if (!user) { navigate('/'); return; }

        // Sedi (per mostrare la sede dell'account inseritore)
        const { data: agenzieData, error: agenzieError } = await supabase
          .from('agenzie')
          .select('id, nome')
          .order('nome', { ascending: true });
        if (agenzieError) throw agenzieError;
        setAgenzieOptions(agenzieData || []);

        // Pacchetti (lookup id -> nome). Nella tabella scartate non c'è FK, quindi mappiamo lato client.
        const { data: pacchettiData, error: pacchettiError } = await supabase
          .from('pacchetti_garanzia')
          .select('id, nome');
        if (pacchettiError) throw pacchettiError;
        const map = {};
        (pacchettiData || []).forEach(p => { map[p.id] = p.nome; });
        setPacchettiById(map);

        // Motivi di scarto distinti (per il filtro a tendina)
        const motiviRows = await fetchAllRows(
          supabase.from('polizze_scartate').select('motivo_scarto')
        );
        const uniqueMotivi = [...new Set(motiviRows.map(r => r.motivo_scarto).filter(Boolean))].sort();
        setMotivoOptions(uniqueMotivi);
      } catch (err) {
        console.error('Errore caricamento dati iniziali (scartate):', err.message);
        openModal('Errore di Caricamento', `Impossibile caricare i dati iniziali: ${err.message}`);
      }
    };
    init();
  }, [navigate]);

  // --- Costruzione query con filtri ---
  const applyFilters = useCallback((q) => {
    if (filters.cliente) {
      const term = `%${filters.cliente}%`;
      q = q.or(`cognome_cliente.ilike.${term},ragione_sociale.ilike.${term},nome_cliente.ilike.${term}`);
    }
    if (filters.codice_fiscale) q = q.ilike('codice_fiscale', `%${filters.codice_fiscale}%`);
    if (filters.targa) q = q.ilike('targa', `%${filters.targa}%`);
    if (filters.motivo_scarto) q = q.eq('motivo_scarto', filters.motivo_scarto);
    if (isISODate(filters.data_da)) q = q.gte('created_at', filters.data_da);
    if (isISODate(filters.data_a)) q = q.lt('created_at', toEndExclusiveISO(filters.data_a));
    return q;
  }, [filters]);

  // --- Fetch adesioni scartate ---
  const fetchScartate = useCallback(async () => {
    setIsLoading(true);
    try {
      let query = supabase
        .from('polizze_scartate')
        .select(`
          *,
          agente:id_agente ( nome, cognome, agenzia_id )
        `, { count: 'exact' });

      query = applyFilters(query);

      const { data, error, count } = await query
        .order('created_at', { ascending: false })
        .order('id', { ascending: false })
        .range((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage - 1);

      if (error) throw error;
      setRows(data || []);
      setTotalRows(count ?? 0);
    } catch (err) {
      console.error('Errore recupero adesioni scartate:', err.message);
      openModal('Errore nel recupero', `Errore nel recupero delle adesioni scartate: ${err.message}`);
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  }, [applyFilters, currentPage]);

  useEffect(() => { fetchScartate(); }, [fetchScartate]);

  // torna a pagina 1 quando cambiano i filtri
  useEffect(() => { setCurrentPage(1); }, [filters]);

  const DATE_FIELDS = new Set(['data_da', 'data_a']);
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setDraftFilters(prev => ({ ...prev, [name]: value }));
    if (!DATE_FIELDS.has(name)) {
      setFilters(prev => ({ ...prev, [name]: value }));
    }
  };
  const commitDateFilters = () => {
    setFilters(prev => ({ ...prev, data_da: draftFilters.data_da, data_a: draftFilters.data_a }));
  };
  const handleDateKeyDown = (e) => {
    if (e.key === 'Enter') { e.currentTarget.blur(); commitDateFilters(); }
  };
  const handleClearFilters = () => {
    setDraftFilters(emptyFilters);
    setFilters(emptyFilters);
  };

  const sedeName = (row) =>
    agenzieOptions.find(a => a.id === row.agente?.agenzia_id)?.nome || 'N/A';
  const clienteName = (row) =>
    row.cliente_privato
      ? `${row.nome_cliente ?? ''} ${row.cognome_cliente ?? ''}`.trim()
      : (row.ragione_sociale ?? '');
  const cfPiva = (row) => (row.cliente_privato ? row.codice_fiscale : row.partita_iva) ?? '';
  const pacchettoName = (row) => pacchettiById[row.id_pacchetto_garanzia] ?? (row.id_pacchetto_garanzia ? '—' : '');

  // --- Export CSV (rispetta i filtri correnti) ---
  const handleExportCsv = useCallback(async () => {
    setIsLoading(true);
    try {
      const allRows = await fetchAllRows(
        applyFilters(
          supabase
            .from('polizze_scartate')
            .select(`*, agente:id_agente ( nome, cognome, agenzia_id )`)
            .order('created_at', { ascending: false })
        )
      );

      if (!allRows.length) {
        openModal('Nessun Dato', 'Nessuna adesione scartata da esportare con i filtri correnti.');
        return;
      }

      const headers = [
        'Data Scarto', 'External ID', 'Sede', 'Account Inseritore', 'Tipologia Cliente',
        'Nome Cliente', 'Cognome Cliente', 'Ragione Sociale', 'Codice Fiscale', 'Partita IVA',
        'Tipo Operazione', 'Tipo Veicolo', 'Modello Auto', 'Data Imm.', 'Targa',
        'Inizio Decorrenza', 'Fine Decorrenza', 'Pacchetto', 'Valore', 'Stato',
        'Motivo Scarto', 'Dettaglio Scarto', 'Giorni Anteriorità'
      ];

      const csvRows = allRows.map(r => [
        `"${formatDateTime(r.created_at)}"`,
        `"${r.external_policy_id ?? ''}"`,
        `"${agenzieOptions.find(a => a.id === r.agente?.agenzia_id)?.nome || 'N/A'}"`,
        `"${r.agente?.nome ?? ''} ${r.agente?.cognome ?? ''}"`.trim() === '' ? '""' : `"${r.agente?.nome ?? ''} ${r.agente?.cognome ?? ''}"`,
        `"${r.cliente_privato ? 'PRIVATO' : 'AZIENDA'}"`,
        `"${r.nome_cliente ?? ''}"`,
        `"${r.cognome_cliente ?? ''}"`,
        `"${r.ragione_sociale ?? ''}"`,
        `"${r.codice_fiscale ?? ''}"`,
        `"${r.partita_iva ?? ''}"`,
        `"${r.tipo_operazione ?? ''}"`,
        `"${r.tipologia_veicolo ?? ''}"`,
        `"${r.modello_auto ?? ''}"`,
        `"${r.data_immatricolazione ?? ''}"`,
        `"${r.targa ?? ''}"`,
        `"${r.inizio_decorrenza ?? ''}"`,
        `"${r.fine_decorrenza ?? ''}"`,
        `"${pacchettiById[r.id_pacchetto_garanzia] ?? ''}"`,
        `"${formatEuro(r.valore_polizza_calcolato)}"`,
        `"${r.stato_polizza ?? ''}"`,
        `"${(r.motivo_scarto ?? '').replace(/"/g, '""')}"`,
        `"${(r.dettaglio_scarto ?? '').replace(/"/g, '""')}"`,
        `"${r.giorni_anteriorita ?? ''}"`,
      ].join(';'));

      const csvContent = [headers.join(';'), ...csvRows].join('\n');
      const blob = new Blob(['﻿' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `adesioni_scartate_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Errore export scartate:', err.message);
      openModal('Errore', `Errore durante l'export: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  }, [applyFilters, agenzieOptions, pacchettiById]);

  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));

  return (
    <div className="max-w-full">
      {/* Intestazione */}
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-mioblu flex items-center gap-3">
          <FaExclamationTriangle className="text-miorosso" /> Adesioni Scartate
        </h1>
        <p className="text-gray-500 mt-1 text-sm">
          Elenco delle adesioni scartate in fase di importazione. Area riservata al Broker.
        </p>
      </div>

      {/* Filtri */}
      <div className="bg-white p-4 md:p-6 rounded-lg shadow-md border border-gray-200 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Cliente */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              <FaUser className="inline mr-1" /> Cliente (cognome / ragione sociale)
            </label>
            <input
              type="text" name="cliente" value={draftFilters.cliente} onChange={handleFilterChange}
              placeholder="Cerca cliente..."
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-mioblu"
            />
          </div>

          {/* Codice fiscale / P.IVA */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              <FaSearch className="inline mr-1" /> Codice Fiscale
            </label>
            <input
              type="text" name="codice_fiscale" value={draftFilters.codice_fiscale} onChange={handleFilterChange}
              placeholder="Cerca CF..."
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-mioblu"
            />
          </div>

          {/* Targa */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              <FaTag className="inline mr-1" /> Targa
            </label>
            <input
              type="text" name="targa" value={draftFilters.targa} onChange={handleFilterChange}
              placeholder="Cerca targa..."
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-mioblu"
            />
          </div>

          {/* Motivo scarto */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              <FaInfoCircle className="inline mr-1" /> Motivo scarto
            </label>
            <select
              name="motivo_scarto" value={draftFilters.motivo_scarto} onChange={handleFilterChange}
              className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-mioblu"
            >
              <option value="">Tutti i motivi</option>
              {motivoOptions.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>

          {/* Data scarto da */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              <FaCalendarAlt className="inline mr-1" /> Data scarto (da)
            </label>
            <input
              type="date" name="data_da" value={draftFilters.data_da}
              onChange={handleFilterChange} onBlur={commitDateFilters} onKeyDown={handleDateKeyDown}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-mioblu"
            />
          </div>

          {/* Data scarto a */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              <FaCalendarAlt className="inline mr-1" /> Data scarto (a)
            </label>
            <input
              type="date" name="data_a" value={draftFilters.data_a}
              onChange={handleFilterChange} onBlur={commitDateFilters} onKeyDown={handleDateKeyDown}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-mioblu"
            />
          </div>
        </div>

        <div className="flex justify-end mt-4 gap-2">
          <button
            type="button" onClick={handleClearFilters}
            className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 transition-colors flex items-center"
          >
            <FaTimes className="mr-2" /> Cancella Filtri
          </button>
          <button
            type="button" onClick={handleExportCsv}
            className="flex items-center gap-2 px-4 py-2 bg-mioblu text-white rounded hover:bg-mioblu/90"
          >
            <FaFileCsv /> ESPORTA CSV
          </button>
        </div>
      </div>

      {/* Tabella */}
      <div className="overflow-x-auto rounded-lg shadow-md border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 table-auto">
          <thead className="bg-gray-50">
            <tr>
              {['Data Scarto', 'External ID', 'Sede', 'Account', 'Tipologia', 'Cliente', 'CF / PI',
                'Tipo Veicolo', 'Modello', 'Targa', 'Decorrenza', 'Pacchetto', 'Valore',
                'Motivo Scarto', 'Gg Ant.', 'Azioni'].map((h) => (
                <th key={h} scope="col"
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {isLoading ? (
              <tr>
                <td colSpan={16} className="px-6 py-10 text-center text-gray-500">
                  <FaSpinner className="animate-spin inline mr-2" /> Caricamento...
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={16} className="px-6 py-10 text-center text-gray-500">
                  Nessuna adesione scartata trovata.
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">{formatDateTime(row.created_at)}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">{row.external_policy_id ?? ''}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">{sedeName(row)}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                    {row.agente ? `${row.agente.nome ?? ''} ${row.agente.cognome ?? ''}`.trim() || 'N/A' : 'N/A'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                    {row.cliente_privato ? 'PRIVATO' : 'AZIENDA'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-800 font-medium">{clienteName(row)}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">{cfPiva(row)}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                    <FaCar className="inline mr-1 text-gray-400" />{row.tipologia_veicolo ?? ''}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">{row.modello_auto ?? ''}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-800 font-semibold">{row.targa ?? ''}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                    {formatDate(row.inizio_decorrenza)}{row.fine_decorrenza ? ` → ${formatDate(row.fine_decorrenza)}` : ''}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">{pacchettoName(row)}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">€ {formatEuro(row.valore_polizza_calcolato)}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className="inline-block px-2 py-1 rounded-full bg-red-100 text-miorosso text-xs font-semibold whitespace-nowrap">
                      {row.motivo_scarto}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 text-center">{row.giorni_anteriorita ?? ''}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-center">
                    <button
                      onClick={() => setDetailRow(row)}
                      title="Vedi dettagli"
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-mioblu text-white text-xs rounded-md hover:bg-mioblu/90 transition-colors"
                    >
                      <FaEye /> Dettagli
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Paginazione */}
        <div className="flex justify-center items-center mt-4 mb-4 gap-2">
          <button
            onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
          >
            ← Precedente
          </button>
          <span>Pagina {currentPage} di {totalPages} <span className="text-gray-400">({totalRows} righe)</span></span>
          <button
            onClick={() => setCurrentPage(p => (p < totalPages ? p + 1 : p))}
            disabled={currentPage >= totalPages}
            className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
          >
            Successiva →
          </button>
        </div>
      </div>

      {/* Modale DETTAGLIO adesione scartata */}
      {detailRow && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => setDetailRow(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* header modale */}
            <div className="flex items-start justify-between p-5 border-b border-gray-200 sticky top-0 bg-white rounded-t-lg">
              <div>
                <h3 className="text-xl font-bold text-mioblu flex items-center gap-2">
                  <FaExclamationTriangle className="text-miorosso" /> Dettaglio adesione scartata
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {clienteName(detailRow) || '—'} · Targa {detailRow.targa || '—'} · {formatDateTime(detailRow.created_at)}
                </p>
              </div>
              <button
                onClick={() => setDetailRow(null)}
                className="text-gray-400 hover:text-gray-700 text-2xl leading-none"
                aria-label="Chiudi"
              >
                <FaTimes />
              </button>
            </div>

            <div className="p-5 space-y-5">
              {/* Blocco motivo/dettaglio scarto in evidenza */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="text-xs font-semibold uppercase text-miorosso mb-1">Motivo scarto</div>
                <div className="text-gray-800 font-medium mb-3">{detailRow.motivo_scarto}</div>
                <div className="text-xs font-semibold uppercase text-miorosso mb-1">Dettaglio scarto</div>
                <div className="text-gray-800 whitespace-pre-wrap break-words">
                  {detailRow.dettaglio_scarto || '—'}
                </div>
                {detailRow.giorni_anteriorita != null && (
                  <div className="mt-3 text-sm text-gray-600">
                    Giorni anteriorità: <span className="font-semibold">{detailRow.giorni_anteriorita}</span>
                  </div>
                )}
              </div>

              {/* Griglia dati anagrafici/veicolo */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                {[
                  ['External Policy ID', detailRow.external_policy_id],
                  ['Stato', detailRow.stato_polizza],
                  ['Sede', sedeName(detailRow)],
                  ['Account inseritore', detailRow.agente ? `${detailRow.agente.nome ?? ''} ${detailRow.agente.cognome ?? ''}`.trim() : 'N/A'],
                  ['Tipologia cliente', detailRow.cliente_privato ? 'PRIVATO' : 'AZIENDA'],
                  ['Tipo operazione', detailRow.tipo_operazione],
                  ['Nome cliente', detailRow.nome_cliente],
                  ['Cognome cliente', detailRow.cognome_cliente],
                  ['Ragione sociale', detailRow.ragione_sociale],
                  ['Codice fiscale', detailRow.codice_fiscale],
                  ['Partita IVA', detailRow.partita_iva],
                  ['Tipo veicolo', detailRow.tipologia_veicolo],
                  ['Modello auto', detailRow.modello_auto],
                  ['Data immatricolazione', formatDate(detailRow.data_immatricolazione)],
                  ['Targa', detailRow.targa],
                  ['Inizio decorrenza', formatDate(detailRow.inizio_decorrenza)],
                  ['Fine decorrenza', formatDate(detailRow.fine_decorrenza)],
                  ['Pacchetto', pacchettoName(detailRow)],
                  ['Valore calcolato', `€ ${formatEuro(detailRow.valore_polizza_calcolato)}`],
                ].map(([label, value]) => (
                  <div key={label} className="flex flex-col">
                    <span className="text-xs uppercase text-gray-400">{label}</span>
                    <span className="text-gray-800 break-words">{value || '—'}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end p-4 border-t border-gray-200">
              <button
                onClick={() => setDetailRow(null)}
                className="px-6 py-2 bg-miorosso text-white rounded-md hover:bg-miorossohover transition-colors"
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modale avvisi/errori */}
      {modal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-mioblu mb-2">{modal.title}</h3>
            <p className="text-gray-700 mb-6">{modal.message}</p>
            <div className="flex justify-end">
              <button
                onClick={closeModal}
                className="px-6 py-2 bg-miorosso text-white rounded-md hover:bg-miorossohover transition-colors"
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
